import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type CommoditiesFormType, getCommoditiesFormSchema } from "Forms/Schemas/GoalsFulfillment/CurrentSolution/commoditiesFormSchema";
import {
  getGetAssetAllocationOverviewQueryKey,
  getListCurrentContractsQueryKey,
  useCreateCurrentContract,
  usePatchCurrentContract,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  CommodityDetailCommodityManufacturer,
  CommodityDetailIngotWeight,
  CreateCurrentContractCommodityBodyInvestmentToolType,
  type CreateCurrentContractCommodityBodyProposedStepActionType,
  type CreateCurrentContractCommodityBodySolutionHorizonType,
  type CurrentContractBaseInvestmentToolType,
  type CurrentContractBaseSolutionHorizonType,
  type CurrentContractDetailCommodity,
  CurrentContractListItemProposedStepActionType,
  type CurrentSolutionAssetOption,
  PatchCurrentContractCommodityBodyInvestmentToolType,
  type PatchCurrentContractCommodityBodyProposedStepActionType,
} from "Infrastructure/ApiZfp/Schema";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useGoalsFulfillmentContext } from "../../GoalsFulfillmentContext";
import { CharacteristicsAccordion } from "../CharacteristicsAccordion";
import type { CurrentSolutionFormHandle } from "./CurrentSolutionWrapperForm";

type Props = {
  readonly edit?: boolean;
  readonly investmentToolType: CurrentContractBaseInvestmentToolType;
  readonly institution?: string;
  readonly horizonType: CurrentContractBaseSolutionHorizonType;
  readonly onSuccess?: () => void;
  readonly currentSolutionId?: string;
  readonly initialData?: CurrentContractDetailCommodity | null;
};

export const CommoditiesForm = forwardRef<CurrentSolutionFormHandle, Props>(
  ({ investmentToolType, institution, edit, horizonType, onSuccess, currentSolutionId, initialData }, ref) => {
    const { t } = useResource();
    const queryClient = useQueryClient();
    const institutionRef = useRef(institution);

    const { analysisId, versionId } = useContext(AnalysisDetailContext);
    const { assetAllocationOverview } = useGoalsFulfillmentContext();

    const { mutate: createMutation } = useCreateCurrentContract();
    const { mutate: updateMutation } = usePatchCurrentContract();

    const brandOptions: Option[] = (
      Object.keys(CommodityDetailCommodityManufacturer) as Array<keyof typeof CommodityDetailCommodityManufacturer>
    ).map((value) => ({
      value,
      name: t(Resources.AnalysisDetail.GoalsFulfillment.BrandOptions[value]),
    }));

    const ingotWeightOptions: Option[] = (Object.keys(CommodityDetailIngotWeight) as Array<keyof typeof CommodityDetailIngotWeight>).map(
      (value) => ({
        value,
        name: t(Resources.AnalysisDetail.GoalsFulfillment.IngotWeightOptions[value]),
      }),
    );

    const proposedStepActionOptions: Option[] = (
      [CurrentContractListItemProposedStepActionType.Keep, CurrentContractListItemProposedStepActionType.Cancel] as Array<
        keyof typeof CurrentContractListItemProposedStepActionType
      >
    ).map((value) => ({
      value,
      name: t(Resources.AnalysisDetail.GoalsFulfillment.CommoditiesSolutionOptions[value]),
    }));

    const methods = useForm<CommoditiesFormType>({
      resolver: yupResolver(getCommoditiesFormSchema(t, assetAllocationOverview?.isPortfolioAssetAllocationRequired)),
      mode: "onChange",
      defaultValues:
        edit && initialData
          ? {
              investmentToolType: initialData.investmentToolType,
              institution: initialData.partnerName ?? "",
              investmentAmount: initialData.oneTimeAmount?.value ?? 0,
              horizon: initialData.currentSolutionHorizon ?? 0,
              appreciationRate: initialData.appreciationRate ?? 0,
              brand: initialData.commodityDetail?.commodityManufacturer as CommodityDetailCommodityManufacturer,
              ingotWeight: initialData.commodityDetail?.ingotWeight as CommodityDetailIngotWeight,
              ingotCount: initialData.commodityDetail?.ingotCount ?? 0,
              entryFee: initialData.entryFee ?? 0,
              regularAmount: initialData.regularAmount?.value ?? 0,
              equities: initialData.assetAllocation?.equities ?? 0,
              bonds: initialData.assetAllocation?.bonds ?? 0,
              commodities: initialData.assetAllocation?.commodities ?? 0,
              realEstate: initialData.assetAllocation?.realEstate ?? 0,
              cash: initialData.assetAllocation?.cash ?? 0,
              other: initialData.assetAllocation?.others ?? 0,
              note: initialData.note ?? "",
              proposedStepAction: initialData.proposedStepActionType,
            }
          : {
              investmentToolType,
              institution,
            },
    });

    const {
      control,
      formState: { errors },
      handleSubmit,
      reset,
      setValue,
    } = methods;

    useEffect(() => {
      if (edit && initialData) {
        reset({
          investmentToolType: initialData.investmentToolType,
          institution: institutionRef.current,
          investmentAmount: initialData.oneTimeAmount?.value ?? 0,
          horizon: initialData.currentSolutionHorizon ?? 0,
          appreciationRate: initialData.appreciationRate ?? 0,
          brand: initialData.commodityDetail?.commodityManufacturer as CommodityDetailCommodityManufacturer,
          ingotWeight: initialData.commodityDetail?.ingotWeight as CommodityDetailIngotWeight,
          ingotCount: initialData.commodityDetail?.ingotCount ?? 0,
          entryFee: initialData.entryFee ?? 0,
          regularAmount: initialData.regularAmount?.value ?? 0,
          equities: initialData.assetAllocation?.equities ?? 0,
          bonds: initialData.assetAllocation?.bonds ?? 0,
          commodities: initialData.assetAllocation?.commodities ?? 0,
          realEstate: initialData.assetAllocation?.realEstate ?? 0,
          cash: initialData.assetAllocation?.cash ?? 0,
          other: initialData.assetAllocation?.others ?? 0,
          note: initialData.note ?? "",
          proposedStepAction: initialData.proposedStepActionType,
        });
      }
    }, [edit, initialData, reset]);

    useEffect(() => {
      setValue("institution", institution ?? "", { shouldValidate: true });
    }, [institution, setValue]);

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        if (edit) {
          handleSubmit(onEdit)();
        } else {
          handleSubmit(onCreate)();
        }
      },
      prefillFromAssetOption: (asset: CurrentSolutionAssetOption) => {
        setValue("investmentToolType", asset.investmentToolType as any, { shouldValidate: true, shouldDirty: true });
        setValue("investmentAmount", asset.amount?.value ?? 0, { shouldValidate: true, shouldDirty: true });
      },
    }));

    const onCreate = (data: CommoditiesFormType) => {
      createMutation(
        {
          analysisId,
          versionId,
          data: {
            investmentToolType: CreateCurrentContractCommodityBodyInvestmentToolType.commodity,
            solutionHorizonType: horizonType as CreateCurrentContractCommodityBodySolutionHorizonType,
            proposedStepActionType: data.proposedStepAction as CreateCurrentContractCommodityBodyProposedStepActionType,
            partnerName: institution ?? "",
            oneTimeAmount: {
              value: data.investmentAmount,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.one_time,
            },
            assetAllocation: {
              equities: data.equities ?? 0,
              bonds: data.bonds ?? 0,
              commodities: data.commodities ?? 0,
              realEstate: data.realEstate ?? 0,
              cash: data.cash ?? 0,
              others: data.other ?? 0,
            },
            currentSolutionHorizon: data.horizon ?? 0,
            appreciationRate: data.appreciationRate,
            note: data.note ?? "",
            regularAmount: {
              value: data.regularAmount ?? 0,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.monthly,
            },
            entryFee: data.entryFee ?? 0,
            commodityDetail: {
              commodityManufacturer: data.brand as CommodityDetailCommodityManufacturer,
              ingotWeight: data.ingotWeight as CommodityDetailIngotWeight,
              ingotCount: data.ingotCount ?? 0,
            },
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListCurrentContractsQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
            onSuccess?.();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
          },
        },
      );
    };

    const onEdit = (data: CommoditiesFormType) => {
      if (!currentSolutionId) return;

      updateMutation(
        {
          analysisId,
          versionId,
          currentContractGuid: currentSolutionId,
          data: {
            investmentToolType: PatchCurrentContractCommodityBodyInvestmentToolType.commodity,
            solutionHorizonType: horizonType,
            partnerName: institution ?? "",
            proposedStepActionType: data.proposedStepAction as PatchCurrentContractCommodityBodyProposedStepActionType,
            oneTimeAmount: {
              value: data.investmentAmount,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.one_time,
            },
            assetAllocation: {
              equities: data.equities ?? 0,
              bonds: data.bonds ?? 0,
              commodities: data.commodities ?? 0,
              realEstate: data.realEstate ?? 0,
              cash: data.cash ?? 0,
              others: data.other ?? 0,
            },
            currentSolutionHorizon: data.horizon ?? 0,
            appreciationRate: data.appreciationRate,
            note: data.note ?? "",
            regularAmount: {
              value: data.regularAmount ?? 0,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.monthly,
            },
            entryFee: data.entryFee ?? 0,
            commodityDetail: {
              commodityManufacturer: data.brand as CommodityDetailCommodityManufacturer,
              ingotWeight: data.ingotWeight as CommodityDetailIngotWeight,
              ingotCount: data.ingotCount ?? 0,
            },
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListCurrentContractsQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
            onSuccess?.();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
          },
        },
      );
    };

    return (
      <FormProvider {...methods}>
        <form>
          <Stack spacing={Spacing.XXL}>
            <Box>
              <Grid container spacing={Spacing.XL}>
                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    name="investmentAmount"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.InvestmentAmount)}
                    errors={errors}
                    labelEndAdornment={<StyledLabelAdornment />}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        {t(Resources.Common.CZKSuffix)}
                      </Typography>
                    }
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                  />
                </Grid>
                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    name="regularAmount"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.RegularAmount)}
                    errors={errors}
                    labelEndAdornment={<StyledLabelAdornment />}
                    inputEndAdornment={
                      <Box sx={{ width: 110 }}>
                        <Typography variant="body2" color="secondary">
                          {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                        </Typography>
                      </Box>
                    }
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                  />
                </Grid>
                <Grid size={4}>
                  <BlFormInput
                    control={control}
                    name="horizon"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Horizon)}
                    errors={errors}
                    labelEndAdornment={<StyledLabelAdornment />}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        {t(Resources.Common.Years)}
                      </Typography>
                    }
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                      },
                    ]}
                  />
                </Grid>
                <Grid size={4}>
                  <BlFormInput
                    control={control}
                    name="appreciationRate"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.AppreciationRate)}
                    labelEndAdornment={<StyledLabelAdornment />}
                    errors={errors}
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        %
                      </Typography>
                    }
                  />
                </Grid>

                <Grid size={4}>
                  <FormDropdown
                    name="brand"
                    options={brandOptions}
                    control={control}
                    labelEndAdornment={<StyledLabelAdornment />}
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.BrandOptions.Title)}
                    errors={errors}
                  />
                </Grid>
                <Grid size={4}>
                  <FormDropdown
                    name="ingotWeight"
                    options={ingotWeightOptions}
                    control={control}
                    labelEndAdornment={<StyledLabelAdornment />}
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.IngotWeightOptions.Title)}
                    errors={errors}
                  />
                </Grid>
                <Grid size={4}>
                  <BlFormInput
                    control={control}
                    name="ingotCount"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.IngotCount)}
                    errors={errors}
                    labelEndAdornment={<StyledLabelAdornment />}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        {t(Resources.Common.Pieces)}
                      </Typography>
                    }
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                      },
                    ]}
                  />
                </Grid>

                <Grid size={4}>
                  <BlFormInput
                    control={control}
                    name="entryFee"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.EntryFee)}
                    errors={errors}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        %
                      </Typography>
                    }
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box>
              <CharacteristicsAccordion<CommoditiesFormType>
                key={assetAllocationOverview?.isPortfolioAssetAllocationRequired ? "open" : "closed"}
                defaultExpanded={assetAllocationOverview?.isPortfolioAssetAllocationRequired}
              />
            </Box>

            <Divider />

            <Box>
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t(Resources.AnalysisDetail.GoalsFulfillment.ProposedSolutionSteps)}
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <FormDropdown
                    name="proposedStepAction"
                    options={proposedStepActionOptions}
                    control={control}
                    labelEndAdornment={<StyledLabelAdornment />}
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.ProposedStepActionTypeOptions.Title)}
                    errors={errors}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <BlFormInput
                    control={control}
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Note)}
                    placeholder={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.NotePlaceholder)}
                    name="note"
                    errors={errors}
                    multiline
                    minRows={6}
                    maxRows={8}
                    characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </form>
      </FormProvider>
    );
  },
);
